import React from 'react';
import pt from 'prop-types';
import semver from 'semver';
import Table from 'components/collection/table';
import Link from 'components/link';
import ButtonLink from 'components/button/link';
import LivePageLink from 'components/link/live-page';
import AppIcon from 'components/apps/icon';
import AppImages from 'components/apps/images';
import ConsoleLink from 'components/console/link';
import { formatCurrency, formatTableValue, imageUrl, nl2br } from 'utils';
import {
  getAppCategoryLabels,
  getAppPageURL,
  getAppScreenshots,
} from 'utils/app';
import './app.scss';

// Enough of the description to recognise it; the rest opens in a modal.
const DESCRIPTION_PREVIEW_LENGTH = 96;

function descriptionPreview(text) {
  return text.length > DESCRIPTION_PREVIEW_LENGTH
    ? `${text.slice(0, DESCRIPTION_PREVIEW_LENGTH)}…`
    : text;
}

export default class AppVersionDetails extends React.PureComponent {
  static propTypes = {
    app: pt.object,
    record: pt.object,
    versionRecord: pt.object,
    showConfigurations: pt.bool,
    showEditAction: pt.bool,
    // Super admin only - the partner dashboard shows this same table read-only
    showCategoriesAction: pt.bool,
    // Super admin only - links the published version to its public marketplace page
    showLivePageLink: pt.bool,
  };

  static contextTypes = {
    openModal: pt.func,
  };

  onClickFullDescription = (event) => {
    const { description, version } = event.currentTarget.dataset;

    this.context.openModal('AppFullDescription', { description, version });
  };

  static defaultProps = {
    showConfigurations: true,
    showEditAction: false,
    showCategoriesAction: false,
    showLivePageLink: false,
  };

  render() {
    const {
      app,
      record,
      versionRecord,
      showConfigurations,
      showEditAction,
      showCategoriesAction,
      showLivePageLink,
    } = this.props;
    const { published_props } = app;
    const isPublishedVersion = Boolean(
      app.public_id && published_props?.version === record.version,
    );

    const appProps =
      published_props?.version &&
      semver.compare(record.version, published_props.version) <= 0
        ? published_props
        : record;

    const categoryLabels = getAppCategoryLabels(record);
    const screenshots = getAppScreenshots(record);

    const integrations = record.integrations || [];

    return (
      <>
        <div className="app-version-details-table">
          <Table
            title={`${record.version} details`}
            titleCount={false}
            fields={[
              {
                path: 'label',
                width: '15%',
              },
              {
                path: 'value',
                className: 'app-version-value',
              },
            ]}
            rows={[
              {
                label: 'Name',
                value: appProps.name,
              },
              {
                label: 'Description',
                value: appProps.description,
              },
              {
                label: 'Price',
                value: (
                  <>
                    {formatCurrency(appProps.price)}{' '}
                    {appProps.price_external && (
                      <>
                        &mdash; external price:{' '}
                        {formatCurrency(appProps.price_external)}
                      </>
                    )}
                  </>
                ),
              },
              ...(appProps.logo_icon
                ? [
                    {
                      label: 'Logo icon',
                      value: (
                        <a href={appProps.logo_icon.url} target="blank">
                          <AppIcon app={appProps} size={60} />
                        </a>
                      ),
                    },
                  ]
                : []),
              ...(appProps.full_description
                ? [
                    {
                      label: 'Full description',
                      // Markdown, and long. A hover tooltip of the raw source
                      // was unreadable, so this opens it rendered in a modal.
                      value: (
                        <button
                          type="button"
                          className="app-version-full-description"
                          data-description={appProps.full_description}
                          data-version={record.version}
                          onClick={this.onClickFullDescription}
                        >
                          {descriptionPreview(appProps.full_description)}
                        </button>
                      ),
                    },
                  ]
                : []),
              {
                label: 'Categories',
                value:
                  categoryLabels.length > 0 || showCategoriesAction ? (
                    <div className="app-version-categories-row">
                      {categoryLabels.length > 0 ? (
                        <ul className="view-body-tags app-version-categories">
                          {categoryLabels.map((label) => (
                            <li key={label} className="nowrap">
                              {label}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        formatTableValue()
                      )}
                      {showCategoriesAction && (
                        <Link
                          to={`/apps/${app.id}/categories`}
                          className="collection-table-link nowrap app-version-categories-edit"
                        >
                          Edit categories
                        </Link>
                      )}
                    </div>
                  ) : null,
              },
              {
                label: 'Integrations',
                value:
                  integrations.length > 0 ? (
                    <ul className="view-body-tags app-version-categories">
                      {integrations.map((integration) => (
                        <li key={integration} className="nowrap">
                          {integration}
                        </li>
                      ))}
                    </ul>
                  ) : null,
              },
              ...(app.type === 'theme'
                ? [
                    {
                      label: 'Highlights',
                      value:
                        appProps.highlights?.length > 0 ? (
                          <div className="app-highlight-list">
                            {appProps.highlights.map(
                              ({ id, title, description }, index) => {
                                const highlightImage =
                                  appProps.highlight_assets?.find(
                                    (asset) => asset.id === id,
                                  )?.image;

                                return (
                                  <div
                                    className="app-highlight wrap"
                                    key={index}
                                  >
                                    <div className="app-highlight-image">
                                      <a
                                        href={highlightImage?.url}
                                        target="blank"
                                      >
                                        <img
                                          src={imageUrl(highlightImage?.url, {
                                            width: 140,
                                            height: 87,
                                            padded: true,
                                          })}
                                          style={{ width: 140, height: 87 }}
                                          alt=""
                                        />
                                      </a>
                                    </div>
                                    <div className="app-highlight-content">
                                      <div className="app-highlight-title">
                                        {title}
                                      </div>
                                      <div className="app-highlight-desc">
                                        {description}
                                      </div>
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        ) : null,
                    },
                  ]
                : []),
            ]}
            action={
              showEditAction ? (
                <ButtonLink
                  type="secondary"
                  size="sm"
                  to={`/apps/${record.id}/edit?version=${record.version}`}
                >
                  Edit details
                </ButtonLink>
              ) : showLivePageLink && isPublishedVersion ? (
                <LivePageLink to={getAppPageURL(app.public_id)} />
              ) : null
            }
          />
        </div>
        {/* Video and screenshots, which every app type can have. The old
            partner form showed these under the same heading. */}
        <Table
          title="Media"
          titleCount={false}
          fields={[
            {
              path: 'label',
              width: '15%',
            },
            {
              path: 'value',
              className: 'app-version-value',
            },
          ]}
          rows={[
            ...(appProps.preview_video_url
              ? [
                  {
                    label: 'Preview video',
                    value: (
                      <iframe
                        width="560"
                        height="315"
                        src={appProps.preview_video_url}
                        title="Preview video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullscreen
                      />
                    ),
                  },
                ]
              : []),
            {
              label: 'Images',
              value:
                screenshots.length > 0 ? (
                  <AppImages images={screenshots} />
                ) : null,
            },
          ]}
        />
        {/* Only a storefront has a preview or a place in the catalog, so
            these are kept out of the shared details above. */}
        {app.type === 'storefront' && (
          <Table
            title="Storefront"
            titleCount={false}
            fields={[
              {
                path: 'label',
                width: '15%',
              },
              {
                path: 'value',
                className: 'app-version-value',
              },
            ]}
            rows={[
              ...(appProps.preview_image
                ? [
                    {
                      label: 'Preview images',
                      value: (
                        <>
                          <a href={appProps.preview_image.url} target="blank">
                            <img
                              src={imageUrl(appProps.preview_image, {
                                width: 600,
                                height: 600,
                                padded: app.type === 'theme' ? false : true,
                              })}
                              style={{ width: 300, height: 300 }}
                              alt=""
                            />
                          </a>{' '}
                          {appProps.preview_mobile_image && (
                            <a
                              href={appProps.preview_mobile_image.url}
                              target="blank"
                            >
                              <img
                                src={imageUrl(appProps.preview_mobile_image, {
                                  width: 200,
                                  height: 400,
                                  padded: app.type === 'theme' ? false : true,
                                })}
                                style={{ width: 150, height: 300 }}
                                alt=""
                              />
                            </a>
                          )}
                        </>
                      ),
                    },
                  ]
                : []),
              {
                label: 'Use cases',
                value:
                  appProps.use_cases?.length > 0 ? (
                    <ul className="view-body-tags">
                      {appProps.use_cases.map((tag, i) => (
                        <li key={i} className="nowrap">
                          {tag}
                        </li>
                      ))}
                    </ul>
                  ) : null,
              },
              {
                label: 'Purchase options',
                type: 'tags',
                value:
                  appProps.purchase_options?.length > 0 ? (
                    <ul className="view-body-tags">
                      {appProps.purchase_options.map((tag, i) => (
                        <li key={i} className="nowrap">
                          {tag}
                        </li>
                      ))}
                    </ul>
                  ) : null,
              },
              {
                label: 'Features',
                type: 'tags',
                value:
                  appProps.features?.length > 0 ? (
                    <ul className="view-body-tags">
                      {appProps.features.map((tag, i) => (
                        <li key={i} className="nowrap">
                          {tag}
                        </li>
                      ))}
                    </ul>
                  ) : null,
              },
            ]}
          />
        )}
        <Table
          title="Support"
          titleCount={false}
          fields={[
            {
              path: 'label',
              width: '15%',
            },
            {
              path: 'value',
              className: 'app-version-value',
            },
          ]}
          rows={[
            {
              label: 'Supported',
              value: versionRecord?.supported ? 'Yes' : 'No',
            },
            {
              label: 'Website',
              value: appProps.support_url ? (
                <a href={appProps.support_url} target="_blank" rel="noreferrer">
                  {appProps.support_url}
                </a>
              ) : null,
            },
            {
              label: 'Documentation',
              value: appProps.documentation_url ? (
                <a
                  href={appProps.documentation_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {appProps.documentation_url}
                </a>
              ) : null,
            },
          ]}
        />
        <Table
          title="Resources"
          titleCount={false}
          fields={[
            {
              path: 'label',
              width: '15%',
              type: '',
            },
            {
              path: 'value',
              className: 'app-version-value',
            },
          ]}
          rows={[
            {
              label: 'Demo URL',
              value: appProps.demo_url ? (
                <a href={appProps.demo_url} target="_blank" rel="noreferrer">
                  {appProps.demo_url}
                </a>
              ) : null,
            },
            {
              label: 'Open source URL',
              value: appProps.repository_url ? (
                <a
                  href={appProps.repository_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {appProps.repository_url}
                </a>
              ) : null,
            },
          ]}
        />
        <Table
          title="Release notes"
          titleCount={false}
          rows={[
            versionRecord?.release_notes ? (
              <div className="app-version-release-notes">
                {nl2br(versionRecord.release_notes)}
              </div>
            ) : null,
          ]}
        />
        {showConfigurations && (
          <div className="app-version-configurations">
            <Table
              title="Configurations"
              titleCount={false}
              fields={[
                {
                  path: 'key',
                },
                {
                  path: 'value',
                  className: 'app-version-value',
                },
                {
                  path: 'action',
                  align: 'right',
                },
              ]}
              rows={[
                'storefront',
                'theme',
                'extensions',
                'permissions',
                'frontend',
              ]
                .map((type) => {
                  const value = versionRecord
                    ? versionRecord[type]
                    : record[type];
                  return (
                    value && {
                      key: type,
                      value: JSON.stringify(value),
                      action: (
                        <ConsoleLink
                          key="console"
                          model="apps"
                          uri={`${record.id}/${type}`}
                          icon={false}
                          buttonType={null}
                          className="collection-table-link nowrap"
                        >
                          View in console
                        </ConsoleLink>
                      ),
                    }
                  );
                })
                .filter(Boolean)}
            />
          </div>
        )}
      </>
    );
  }
}
